import React, { useState, useEffect } from 'react';
import { ExternalLink, Edit2, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ChatInterface } from '../components/chat/ChatInterface';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardStats } from '../components/dashboard/DashboardStats';
import { OnboardingModal } from '../components/dashboard/OnboardingModal';
import { ConversationLimitAlert } from '../components/subscription/ConversationLimitAlert';
import { useAuth } from '../contexts/AuthContext';
import { useChatbotConfig } from '../contexts/ChatbotConfigContext';
import { useChatbot } from '../contexts/ChatbotContext';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { getChatbotConfigSection } from '../utils/chatbotConfigAccess';

interface WidgetSettings {
  showOnboarding?: boolean;
  [key: string]: unknown;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { chatbotPublicId, chatbotUserId, isLoading: isChatbotLoading } = useChatbot();
  const { refetchConfig } = useChatbotConfig();
  const { subscription, initializeSubscription, initialized } = useSubscription();
  
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [showLimitReached, setShowLimitReached] = useState(false);
  const [remainingConversations, setRemainingConversations] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [chatbotName, setChatbotName] = useState<string>('My First Chatbot');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newChatbotName, setNewChatbotName] = useState<string>('');
  const [showStats, setShowStats] = useState(false);

  // Initialize subscription if needed
  useEffect(() => {
    if (!initialized && user) {
      initializeSubscription();
    }
  }, [initialized, initializeSubscription, user]);

  // Fetch config and initialize settings when component mounts
  useEffect(() => {
    const initializeDashboard = async () => {
      if (!user || !chatbotPublicId || isChatbotLoading || !subscription) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Refetch the complete config to ensure all settings are up to date
        await refetchConfig();

        const widgetSettings = await getChatbotConfigSection<WidgetSettings>(
          'widget_settings',
          chatbotPublicId
        );
        setShowOnboarding(widgetSettings?.showOnboarding !== false);

        // Get conversation usage
        const { data: usageData } = await supabase
          .from('conversation_usage')
          .select('conversation_count')
          .eq('user_id', user.id)
          .eq('month_start', new Date().toISOString().slice(0, 7) + '-01')
          .maybeSingle();

        // Get conversation limit based on subscription tier
        const { data: limitData } = await supabase
          .from('feature_limits')
          .select('max_limit')
          .eq('tier', subscription.tier || 'free')
          .eq('feature', 'conversations_per_month')
          .maybeSingle();

        if (usageData && limitData) {
          const currentCount = usageData.conversation_count;
          const maxLimit = limitData.max_limit;
          const remaining = maxLimit - currentCount;

          setRemainingConversations(remaining);
          
          // Show warning when less than 10 conversations remaining
          if (remaining <= 10 && remaining > 0) {
            setShowLimitWarning(true);
          }
          
          // Show limit reached alert when no conversations remaining
          if (remaining <= 0) {
            setShowLimitReached(true);
          }
        }

        // Fetch chatbot name
        const { data: chatbotData } = await supabase
          .from('chatbots')
          .select('name')
          .eq('public_id', chatbotPublicId)
          .single();

        if (chatbotData?.name) {
          setChatbotName(chatbotData.name);
          setNewChatbotName(chatbotData.name);
        }
      } catch (err) {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    };

    initializeDashboard();
  }, [user, chatbotPublicId, isChatbotLoading, subscription, refetchConfig, initialized]);

  const handleUpdateChatbotName = async () => {
    if (!chatbotPublicId || !newChatbotName.trim()) return;
    
    try {
      const { error } = await supabase
        .from('chatbots')
        .update({ name: newChatbotName.trim() })
        .eq('public_id', chatbotPublicId);
      
      if (error) throw error;
      
      setChatbotName(newChatbotName.trim());
      setIsEditingName(false);
    } catch (err) {
      // Error handled silently
    }
  };

  const publicUrl = chatbotPublicId 
    ? `https://botmaker-agency.ai/widget?id=${chatbotPublicId}&theme=dark`
    : null;

  if (isLoading || isChatbotLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-3/4 mb-8"></div>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[60%] h-96 bg-gray-800 rounded"></div>
            <div className="lg:w-[40%] h-96 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DashboardHeader />
      
      <div className="mb-8">
        <div className="flex items-center gap-3">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newChatbotName}
                onChange={(e) => setNewChatbotName(e.target.value)}
                className="text-2xl font-bold text-white bg-white/5 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
              <button
                onClick={handleUpdateChatbotName}
                className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
              >
                <Check className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setIsEditingName(false);
                  setNewChatbotName(chatbotName);
                }}
                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white">{chatbotName}</h1>
              <button
                onClick={() => setIsEditingName(true)}
                className="p-1 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                title="Edit chatbot name"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        <p className="text-gray-400 mt-2">
          Here is a preview of your chatbot and its performance
          {publicUrl && (
            <>
              {' - '}
              <a 
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1"
              >
                public link <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </p>
      </div>

      {/* Bouton pour afficher les stats (mobile uniquement) */}
      <div className="lg:hidden flex justify-end mb-3">
        <button
          onClick={() => setShowStats(true)}
          className="inline-flex items-center px-3 py-1.5 text-xs rounded-full bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 hover:text-indigo-200 transition-colors shadow-sm"
        >
          <span>Statistics</span>
          <ChevronLeft className="ml-1 w-3 h-3" />
        </button>
      </div>

      {/* Conteneur principal avec chatbot et stats */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Chatbot - toujours visible */}
        <div className="w-full lg:w-[60%]">
          <div className="h-[500px] lg:h-[550px] rounded-xl overflow-hidden">
            <ChatInterface />
          </div>
        </div>
        
        {/* Stats sur desktop - à droite du chat */}
        <div className="hidden lg:block lg:w-[40%]">
          <DashboardStats />
        </div>
      </div>
      
      {/* Panneau de stats sur mobile */}
      <div className={`
        fixed inset-0 pointer-events-none z-50
        ${showStats ? 'opacity-100' : 'opacity-0 invisible'}
        transition-opacity duration-300
      `}>
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-black/40 pointer-events-auto"
          onClick={() => setShowStats(false)}
        />
        
        {/* Panneau coulissant */}
        <div className={`
          absolute right-0 top-[70px] bottom-0 w-[85%] sm:w-[65%] md:w-[55%]
          bg-gray-900/95 backdrop-blur pointer-events-auto
          shadow-xl border-l border-white/10
          transform transition-transform duration-300 ease-out
          ${showStats ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}>
          {/* En-tête du panneau */}
          <div className="p-4 border-b border-gray-800 flex items-center justify-between sticky top-0 z-10 bg-gray-800/80 backdrop-blur">
            <h3 className="text-base font-medium text-white">Performance Statistics</h3>
            <button 
              onClick={() => setShowStats(false)}
              className="p-1.5 rounded-full bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {/* Contenu des stats avec défilement */}
          <div className="flex-1 overflow-y-auto p-5">
            <DashboardStats />
          </div>
        </div>
      </div>

      {showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}

      <ConversationLimitAlert
        isOpen={showLimitWarning}
        onClose={() => setShowLimitWarning(false)}
        type="warning"
        remainingConversations={remainingConversations}
      />

      <ConversationLimitAlert
        isOpen={showLimitReached}
        onClose={() => setShowLimitReached(false)}
        type="limit"
      />
    </div>
  );
}